"use client";

import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { useStore } from "@/store";
import { trackEvent, useTrackEventOnce } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

export const MathEditor = observer(function MathEditor({
  children,
}: {
  children?: React.ReactNode;
}) {
  const { auth } = useStore();

  return (
    <Link
      href={auth.isLoggedIn ? "/doc" : "/signup"}
      className="inline-block text-blue-600 relative group active:translate-y-[1px]"
      onClick={() => trackEvent("about_get_started_clicked", { button: "math_editor" })}
    >
      <span
        className={twMerge(
          "inline-block text-blue-600 bg-blue-100 rounded-[20px] z-[-1] left-[-13px] right-[-23px] bottom-[-7px] top-[-5px] absolute group-hover:bg-blue-200",
          "xl:top-[-2px]",
          "sm:rounded-[10px] sm:right-[-13px] sm:top-[-3px] sm:bottom-[-4px]",
        )}
      />
      {children}
    </Link>
  );
});

export const LoginOrSignup = observer(function LoginOrSignup({
  redirectToApp,
  baseColor = "blue",
}: {
  redirectToApp?: boolean;
  baseColor?: "blue" | "green";
}) {
  const { auth } = useStore();
  const router = useRouter();
  const t = useTranslations();

  useTrackEventOnce("about_page_viewed", {});

  useEffect(() => {
    if (auth.isLoggedIn && redirectToApp) {
      router.push("/doc");
    }
  }, [auth.isLoggedIn, redirectToApp, router]);

  if (auth.isLoggedIn) {
    return (
      <Link
        href="/doc"
        className={twMerge(
          "py-[8px] px-[20px] rounded-10 bg-blue-100 text-blue-500 font-medium",
          "active:translate-y-[1px] hover:bg-blue-200 focus:bg-blue-200 ",
          baseColor === "green" && "bg-green-400 text-black hover:bg-green-500 focus:bg-green-500",
        )}
        onClick={() => trackEvent("about_get_started_clicked", { button: "top_get_started" })}
      >
        {t("about.getStarted")}
      </Link>
    );
  }

  return (
    <div className="text-[18px] tracking-[-0.36px] leading-[1.3] flex gap-[10px]">
      <Link
        href="/login"
        className="py-[8px] px-[20px] rounded-10 hover:underline md:hidden"
        onClick={() => trackEvent("about_get_started_clicked", { button: "login" })}
      >
        {t("about.signIn")}
      </Link>
      <Link
        href="/signup"
        className={twMerge(
          "py-[8px] px-[20px] rounded-10 bg-blue-100 text-blue-500 font-medium active:translate-y-[1px] hover:bg-blue-200 dark:hover:bg-blue-800 focus:bg-blue-200 dark:focus:bg-blue-800",
          "sm:text-[16px] sm:trailing-[-0.32px] sm:p-[10px] sm:pt-[7px] sm:pb-[6px] sm:leading-[1.3]",
          baseColor === "green" && "bg-green-400 text-black hover:bg-green-500 focus:bg-green-500",
        )}
        onClick={() => trackEvent("about_get_started_clicked", { button: "signup" })}
      >
        {t("about.signUp")}
      </Link>
    </div>
  );
});

export function GetStarted({ baseColor = "blue" }: { baseColor?: "blue" | "green" }) {
  const t = useTranslations();
  return (
    <Link
      href="/doc"
      className={twMerge(
        "pb-[13px] pt-[12px] px-[50px] rounded-10 bg-blue-100 text-blue-500",
        "text-[26px] trailing-[1.3] tracking-[-0.52px] font-medium",
        "inline-block mt-[10px]",
        "active:translate-y-[1px] hover:bg-blue-200 focus:bg-blue-200",
        "sm:text-[22px] sm:tracking-[-0.44px] sm:mt-0 sm:pt-[10px] sm:pb-[10px] sm:px-[40px]",
        baseColor === "green" && "bg-green-400 text-black hover:bg-green-500 focus:bg-green-500",
      )}
      onClick={() => trackEvent("about_get_started_clicked", { button: "get_started" })}
    >
      {t("about.getStarted")}
      <span className="font-mono inline-block ml-[9px] mt-[1px]">&rarr;</span>
    </Link>
  );
}
